import { camelCase, startCase } from 'es-toolkit'

import type { OuraUser } from '#web-oura-tools/types/user'

import { normalizeGroupKey } from '#web-oura-tools/auth/oura-user'

export class Permission {
  readonly key: string
  readonly name: string
  readonly description: string
  readonly normalizedKey: string

  private readonly _user: OuraUser

  public constructor(key: string, description: string, user: OuraUser, normalizedKey?: string) {
    this.key = key
    this.description = description
    this.normalizedKey = normalizedKey ?? camelCase(key)
    this.name = startCase(key)
      .replace(/\b(?:Allow|Roles)\b/g, '')
      .replace(/\s+/g, ' ')
      .trim()
    this._user = user
  }

  /** true if user has the right (or group membership) and it's not overridden */
  public get isGranted(): boolean {
    return this._user.activeRights.includes(this.normalizedKey) || this._user.activeGroups.includes(this.normalizedKey)
  }

  /**
   * true if the user holds this right or group membership, ignoring local overrides.
   * Does NOT reflect effective access — never use this to check permissions. Intended only
   * for the permissions menu, to decide toggle availability.
   */
  public get isGrantedIgnoringOverrides(): boolean {
    return (
      this._user.rights.includes(this.normalizedKey) ||
      this._user.groups.some((g) => normalizeGroupKey(g) === this.normalizedKey)
    )
  }

  public get isOverridden(): boolean {
    return this._user.overrides.includes(this.normalizedKey)
  }
}

export class PermissionGroup {
  readonly key: string
  readonly name: string
  readonly allow: Permission[]
  readonly deny: Permission[]
  readonly isMyGroup: boolean
  readonly isPseudo: boolean

  public constructor(key: string, allow: Permission[], deny: Permission[], isMyGroup: boolean, isPseudo = false) {
    this.key = key
    this.name = isPseudo ? key : startCase(key)
    this.allow = allow
    this.deny = deny
    this.isMyGroup = isMyGroup
    this.isPseudo = isPseudo
  }
}

export default Permission
